/*
 * BottomBarMenu
 * @module BottomBar
 * @flow
 */

import React from 'react';
import styled, { css } from 'styled-components/macro';
import { palette } from 'styled-theme';
import { ifProp } from 'styled-tools';

import { LanguageSelect } from '~/components';

export type BottomBarMenuProps = {
  children: () => React$Node,
  locale: String,
  icon: React$Element<*>,
  isVisible: boolean,
  items: React$Element<*>,
  menuRef: React$ElementRef<*>,
  palette?: string,
  version?: String,
  onButtonTouch: () => void,
};

const baseColor = palette('grayscale', 0, true);

const BottomBarMenuWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  flex: 1;
`;

const BottomBarMenuButton = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  color: ${baseColor};
  flex-direction: column;
  justify-content: center;
  opacity: 0.8;

  ${ifProp(
    'isVisible',
    css`
      background-color: ${palette(2)};
      border-bottom: 2px solid ${baseColor};
      box-sizing: border-box;
      opacity: 1;
    `,
  )}
`;

const BottomBarMenuIcon = styled.span`
  margin-bottom: 4px;
`;

const BottomBarMenuTitle = styled.span`
  font-size: 10px;
`;

const BottomBarMenuDropdown = styled.div`
  position: absolute;
  bottom: 60px;
  right: 0;
  display: ${ifProp('isVisible', 'block', 'none')};
  padding: 7px 24px;
  width: ${ifProp({ locale: 'en' }, '240px', '188px')};
  background: ${palette(7)};
  box-sizing: border-box;
`;

const BottomBarMenuLanguageSelect = styled.div`
  position: relative;
  margin-bottom: 12px;
  width: 50px;
  border-bottom: 2px solid rgba(255, 255, 255, 0.5);

  .react-select {
    width: 100%;

    &__control {
      &,
      &--is-focused {
        border-bottom: none;
      }
    }

    &__menu {
      width: 140px;
    }
  }
`;

const BottomBarMenuDropdownScrollWrapper = styled.div`
  max-height: calc(100vh - 120px);
  overflow-y: scroll;
`;

const Version = styled.div`
  font-size: 10px;
  color: #fff;
  opacity: 0.6;
  position: absolute;
  right: 4px;
  bottom: 4px;
`;

/**
 * BottomBarMenu React-component
 * @memberof components
 * @param {Object} props - properties
 * @param {Object} [props.locale] - locale
 * @param {React.Children} [props.children] - children
 * @param {React.Node<*>} [props.icon] - icon
 * @param {Function} [props.isVisible] - visibility flag
 * @param {React.Node<*>} [props.items] - items
 * @param {React$ElementRef<*>} [props.menuRef] - menu element reference
 * @param {Function} [props.palette] - palette
 * @param {Function} [props.onButtonTouch] - button touch handler
 * @example <caption>Simple usage</caption>
 * <BottomBarMenu icon={<IconWallet />}>My Wealth</BottomBarMenu>
 * @returns {React.Node<*>} React-component
 */
export const BottomBarMenu = ({
  children,
  locale,
  icon,
  isVisible,
  items,
  menuRef,
  palette,
  version,
  onButtonTouch,
}: BottomBarMenuProps) => (
  <BottomBarMenuWrapper ref={menuRef} testid="bottom-bar-menu">
    <BottomBarMenuButton
      isVisible={isVisible}
      palette={palette}
      onTouchStart={onButtonTouch}
    >
      <BottomBarMenuIcon>{icon}</BottomBarMenuIcon>
      <BottomBarMenuTitle>{children}</BottomBarMenuTitle>
    </BottomBarMenuButton>
    <BottomBarMenuDropdown
      locale={locale}
      isVisible={isVisible}
      palette={palette}
    >
      <BottomBarMenuDropdownScrollWrapper>
        {items}
      </BottomBarMenuDropdownScrollWrapper>
      <BottomBarMenuLanguageSelect>
        <LanguageSelect />
      </BottomBarMenuLanguageSelect>
      <Version>{version}</Version>
    </BottomBarMenuDropdown>
  </BottomBarMenuWrapper>
);

BottomBarMenu.defaultProps = {
  palette: 'primary',
};
